import React, { useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileCode, FileText, Zap, Workflow, Database, Layout, Archive, Copy, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@ncoder/ui';
import Editor, { DiffEditor } from '@monaco-editor/react';
import { useTheme } from 'next-themes';
import { toast } from 'react-hot-toast';
import { useUserBranches } from '@/hooks/metadata_v1';
import { getMonacoLanguage } from '@ncoder/ui';

const getFileIcon = (type: string) => {
  const iconClass = "h-5 w-5";
  switch (type) {
    case 'ApexClass':
      return <FileCode className={`${iconClass} text-blue-500`} />;
    case 'ApexTrigger':
      return <Zap className={`${iconClass} text-purple-500`} />;
    case 'LightningComponent':
    case 'LightningComponentBundle':
    case 'AuraDefinitionBundle':
      return <Layout className={`${iconClass} text-cyan-500`} />;
    case 'Flow':
      return <Workflow className={`${iconClass} text-green-500`} />;
    case 'CustomObject':
      return <Database className={`${iconClass} text-orange-500`} />;
    case 'VisualforcePage':
    case 'ApexPage':
      return <FileText className={`${iconClass} text-indigo-500`} />;
    case 'StaticResource':
      return <Archive className={`${iconClass} text-gray-500`} />;
    default:
      return <FileCode className={`${iconClass} text-gray-500`} />;
  }
};

export const FileDetails: React.FC = () => {
  const navigate = useNavigate();
  const { fileId } = useParams<{ fileId: string }>();
  const { theme } = useTheme();
  const editorRef = useRef<any>(null);
  
  // Reuse cached userBranches to get both content sides for diff
  const { data: branches, isLoading, error } = useUserBranches({ status: 'ACTIVE' });
  const branch = branches?.find((b) => b.metadataItem.id === fileId);

  // Cleanup on unmount (e.g. browser back button) — dispose before nullifying to prevent "TextModel got disposed" error
  useEffect(() => {
    return () => {
      if (editorRef.current) {
        try { editorRef.current.dispose(); } catch { /* already disposed */ }
        editorRef.current = null;
      }
    };
  }, []);

  // Handle editor mount
  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  // Define custom themes for Monaco Editor
  const handleEditorWillMount = (monaco: any) => {
    // Define custom dark theme with pitch black background
    monaco.editor.defineTheme('custom-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#000000',
        'editor.lineHighlightBackground': '#1a1a1a',
        'editorLineNumber.foreground': '#666666',
        'editorLineNumber.activeForeground': '#ffffff',
        // Diff colors for dark theme
        'diffEditor.insertedTextBackground': '#1a4d1a33',
        'diffEditor.removedTextBackground': '#4d1a1a33',
        'diffEditor.insertedLineBackground': '#1a4d1a1a',
        'diffEditor.removedLineBackground': '#4d1a1a1a',
        'editorOverviewRuler.addedForeground': '#22c55e',
        'editorOverviewRuler.deletedForeground': '#ef4444',
        'editorOverviewRuler.modifiedForeground': '#f59e0b',
        'editorGutter.addedBackground': '#22c55e',
        'editorGutter.deletedBackground': '#ef4444',
        'editorGutter.modifiedBackground': '#f59e0b',
      }
    });

    // Define custom light theme with white background
    monaco.editor.defineTheme('custom-light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editor.background': '#ffffff',
        'editor.lineHighlightBackground': '#f5f5f5',
        'editorLineNumber.foreground': '#999999',
        'editorLineNumber.activeForeground': '#000000',
        // Diff colors for light theme
        'diffEditor.insertedTextBackground': '#22c55e22',
        'diffEditor.removedTextBackground': '#ef444422',
        'diffEditor.insertedLineBackground': '#22c55e11',
        'diffEditor.removedLineBackground': '#ef444411',
        'editorOverviewRuler.addedForeground': '#22c55e',
        'editorOverviewRuler.deletedForeground': '#ef4444',
        'editorOverviewRuler.modifiedForeground': '#f59e0b',
        'editorGutter.addedBackground': '#22c55e',
        'editorGutter.deletedBackground': '#ef4444',
        'editorGutter.modifiedBackground': '#f59e0b',
      }
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading file diff...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <h2 className="text-2xl font-bold text-foreground">Failed to load file</h2>
          <p className="text-sm text-muted-foreground">{error.message}</p>
          <Button onClick={handleBackNavigation}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Deployment
          </Button>
        </div>
      </div>
    );
  }

  // Not found state
  if (!branch) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 max-w-md text-center">
          <FileCode className="h-12 w-12 text-muted-foreground" />
          <h2 className="text-2xl font-bold text-foreground">File Not Found</h2>
          <p className="text-sm text-muted-foreground">The requested file could not be found.</p>
          <Button onClick={handleBackNavigation}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Deployment
          </Button>
        </div>
      </div>
    );
  }

  // branch.metadataItem.content = Salesforce/main (original)
  // branch.content = NCoder branch (modified)
  const fileName = branch.metadataItem.fullName;
  const fileType = branch.metadataItem.metadataType;
  const originalCode = branch.metadataItem.content || '';
  const modifiedCode = branch.content || '';
  const language = getMonacoLanguage(fileType, fileName);

  const handleCopy = (code: string, label: string) => {
    navigator.clipboard.writeText(code);
    toast.success(`${label} code copied to clipboard!`);
  };

  const getLineCount = (code: string) => {
    return code.split('\n').length;
  };

  const handleBackNavigation = () => {
    // Dispose editor before navigation
    if (editorRef.current) {
      editorRef.current.dispose();
      editorRef.current = null;
    }
    navigate('/deploy');
  };

  return (
    <div className="h-full flex flex-col gap-4">
      {/* Header with Back Button */}
      <div className="bg-background">
        <div className="flex items-center gap-2">
          {/* Back Button */}
          <Button
            variant="outline"
            size="default"
            onClick={handleBackNavigation}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="font-medium">Back</span>
          </Button>
          
          {/* Divider */}
          <div className="h-8 w-px bg-border" />
          
          {/* File Info */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            <div className="p-2.5 rounded-lg bg-primary/10 border border-primary/20">
              {getFileIcon(fileType)}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl font-semibold text-foreground truncate">
                {fileName}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">{fileType}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Diff View - Two Panel Layout */}
      <div className="flex-1 overflow-hidden flex flex-col border border-border rounded-lg">
        {originalCode || modifiedCode ? (
          <>
            {/* Header with both panel titles */}
            <div className="flex items-center bg-muted/50 border-b border-border">
              {/* Left Panel Header */}
              <div className="flex-1 flex items-center justify-between px-4 py-3 border-r border-border">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                  <span className="font-semibold text-sm">Salesforce (Current)</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {getLineCount(originalCode)} lines
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(originalCode, 'Original')}
                  className="h-8 w-8 p-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Right Panel Header */}
              <div className="flex-1 flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="font-semibold text-sm">NCoder (New)</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    {getLineCount(modifiedCode)} lines
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleCopy(modifiedCode, 'Modified')}
                  className="h-8 w-8 p-0"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {/* Diff Editor */}
            <div className="flex-1 overflow-hidden">
              <DiffEditor
                height="100%"
                language={language}
                original={originalCode}
                modified={modifiedCode}
                theme={theme === 'dark' ? 'custom-dark' : 'custom-light'}
                beforeMount={handleEditorWillMount}
                onMount={handleEditorDidMount}
                options={{
                  readOnly: true,
                  renderSideBySide: true,
                  minimap: { enabled: false },
                  fontSize: 14,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  wordWrap: 'off',
                  automaticLayout: true,
                  scrollbar: {
                    vertical: 'visible',
                    horizontal: 'visible',
                    verticalScrollbarSize: 6,
                    horizontalScrollbarSize: 6,
                    useShadows: false,
                  },
                  renderOverviewRuler: true,
                  diffWordWrap: 'off',
                  ignoreTrimWhitespace: false,
                  renderIndicators: true,
                }}
              />
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center w-full h-full">
            <p className="text-muted-foreground">No code diff available for this file</p>
          </div>
        )}
      </div>
    </div>
  );
};

